import { useCallback, useEffect, useRef, useState } from "react";
import { Upload, Loader2, Download, Copy, Check, Star, Lock, RotateCcw } from "lucide-react";
import api, { API, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { fmtSize, fmtDate } from "./util";

const SHARE_EXPIRY = [
  { value: "1", label: "1 hour" }, { value: "24", label: "24 hours" },
  { value: "168", label: "7 days" }, { value: "720", label: "30 days" },
];

export function ShareDialog({ doc, onClose }) {
  const [expiry, setExpiry] = useState("24");
  const [url, setUrl] = useState("");
  const [generating, setGenerating] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => { setUrl(""); setCopied(false); setExpiry("24"); }, [doc]);

  const generate = async () => {
    setGenerating(true); setCopied(false);
    try {
      const { data } = await api.post(`/documents/${doc.doc_id}/share`, { expires_in_hours: Number(expiry) });
      setUrl(`${API.replace(/\/api$/, "")}${data.path}`);
      toast.success("Share link created");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Could not create link"); }
    finally { setGenerating(false); }
  };
  const copy = async () => {
    try { await navigator.clipboard.writeText(url); setCopied(true); toast.success("Link copied"); setTimeout(() => setCopied(false), 2000); }
    catch { toast.error("Could not copy — select manually"); }
  };

  return (
    <Dialog open={!!doc} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#121214] border-white/10 text-white max-w-md" data-testid="share-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl pr-8">Share document</DialogTitle>
          <DialogDescription className="text-[#71717A] truncate">{doc?.filename}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <label className="text-xs text-[#A1A1AA] mb-1.5 block">Link expires in</label>
            <Select value={expiry} onValueChange={setExpiry}>
              <SelectTrigger className="bg-[#0d0d0f] border-white/10 text-white" data-testid="share-expiry-select"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#1C1C1F] border-white/10 text-white">
                {SHARE_EXPIRY.map((o) => <SelectItem key={o.value} value={o.value} className="focus:bg-white/10">{o.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {url && (
            <div className="flex items-center gap-2">
              <input readOnly value={url} data-testid="share-url-input" className="flex-1 bg-[#0d0d0f] border border-white/10 rounded-lg px-3 h-10 text-xs text-[#D4D4D8] focus:outline-none" />
              <button onClick={copy} data-testid="share-copy-btn" className="w-10 h-10 shrink-0 rounded-lg fv-gold-gradient text-[#0A0A0B] flex items-center justify-center hover:opacity-90">
                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              </button>
            </div>
          )}
          <Button onClick={generate} disabled={generating} data-testid="share-generate-btn" className="w-full fv-gold-gradient text-[#0A0A0B] font-semibold rounded-full hover:opacity-90">
            {generating ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating…</> : url ? "Regenerate link" : "Generate link"}
          </Button>
          <p className="text-[11px] text-[#52525B] leading-relaxed">Anyone with this link can view the document until it expires. The link stops working automatically.</p>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function VersionsDialog({ doc, canVersion, onClose, onChanged }) {
  const [versions, setVersions] = useState([]);
  const [uploading, setUploading] = useState(false);
  const verFileRef = useRef(null);

  const load = useCallback(() => {
    if (!doc) return;
    api.get(`/documents/${doc.doc_id}/versions`).then(({ data }) => setVersions(data.versions)).catch(() => {});
  }, [doc]);
  useEffect(() => { load(); }, [load]);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const form = new FormData(); form.append("file", file);
    try {
      await api.post(`/documents/${doc.doc_id}/version`, form, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("New version uploaded"); load(); onChanged();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Upload failed"); }
    finally { setUploading(false); if (verFileRef.current) verFileRef.current.value = ""; }
  };
  const restore = async (v) => {
    try { await api.post(`/documents/${doc.doc_id}/versions/${v}/restore`); toast.success(`Restored v${v}`); load(); onChanged(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Restore failed"); }
  };
  const openVersion = (v) => window.open(`${API.replace(/\/api$/, "")}/api/documents/${doc.doc_id}/versions/${v}/download`, "_blank");

  return (
    <Dialog open={!!doc} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#121214] border-white/10 text-white max-w-md" data-testid="versions-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl pr-8">Version history</DialogTitle>
          <DialogDescription className="text-[#71717A] truncate">{doc?.filename}</DialogDescription>
        </DialogHeader>
        {!canVersion && (
          <div className="rounded-xl border border-[#D4AF37]/30 bg-[#151310] p-3 text-xs text-[#D4AF37] mb-2" data-testid="version-gate">
            Version history is available on the Growth plan and above. Upgrade to keep a full revision trail.
          </div>
        )}
        <div className="space-y-2 mb-4 max-h-72 overflow-y-auto" data-testid="versions-list">
          {versions.map((v) => (
            <div key={v.version} className={`flex items-center justify-between rounded-xl border p-3 ${v.is_current ? "border-[#D4AF37]/25 bg-[#151310]" : "border-white/8 bg-[#0d0d0f]"}`} data-testid={`version-row-${v.version}`}>
              <div className="min-w-0">
                <div className="text-sm text-white flex items-center gap-2">v{v.version}{v.is_current && <span className="text-[9px] uppercase tracking-wider text-[#D4AF37]">Current</span>}</div>
                <div className="text-xs text-[#71717A] truncate">{v.filename} · {fmtSize(v.size)} · {fmtDate(v.created_at)}</div>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                {!v.is_current && <button onClick={() => restore(v.version)} data-testid={`version-restore-${v.version}`} title="Restore this version" className="w-8 h-8 rounded-full bg-white/5 border border-white/15 text-[#71717A] hover:text-emerald-400 flex items-center justify-center"><RotateCcw className="w-4 h-4" /></button>}
                <button onClick={() => openVersion(v.version)} data-testid={`version-download-${v.version}`} title="Download" className="w-8 h-8 rounded-full bg-white/5 border border-white/15 text-[#71717A] hover:text-[#D4AF37] flex items-center justify-center"><Download className="w-4 h-4" /></button>
              </div>
            </div>
          ))}
        </div>
        <input ref={verFileRef} type="file" onChange={onFile} className="hidden" data-testid="version-file-input" />
        <Button onClick={() => verFileRef.current?.click()} disabled={uploading || !canVersion} data-testid="upload-version-btn" className="w-full fv-gold-gradient text-[#0A0A0B] font-semibold rounded-full hover:opacity-90 disabled:opacity-50">
          {uploading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Uploading…</> : <><Upload className="w-4 h-4 mr-2" /> Upload new version</>}
        </Button>
      </DialogContent>
    </Dialog>
  );
}

export function DetailDialog({ doc, meta, folders, onClose, onSaved }) {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!doc) { setForm(null); return; }
    setForm({
      filename: doc.filename, category: doc.category, status: doc.status,
      folder_id: doc.folder_id || "root", tags: (doc.tags || []).join(", "),
      notes: doc.notes || "", favorite: !!doc.favorite, locked: !!doc.locked,
      expiration_date: doc.expiration_date || "",
    });
  }, [doc]);

  if (!doc || !form) return null;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        filename: form.filename, category: form.category, status: form.status,
        folder_id: form.folder_id === "root" ? null : form.folder_id,
        tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
        notes: form.notes, favorite: form.favorite, locked: form.locked,
        expiration_date: form.expiration_date || null,
      };
      await api.patch(`/documents/${doc.doc_id}`, payload);
      toast.success("Document updated"); onSaved(); onClose();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Update failed"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#121214] border-white/10 text-white max-w-lg max-h-[88vh] overflow-y-auto" data-testid="detail-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl pr-8">Document details</DialogTitle>
          <DialogDescription className="text-[#71717A]">{fmtSize(doc.size)} · {doc.content_type}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3.5">
          <Field label="Name"><input value={form.filename} onChange={(e) => set("filename", e.target.value)} data-testid="detail-name" className={inputCls} /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Category">
              <Select value={form.category} onValueChange={(v) => set("category", v)}>
                <SelectTrigger className="bg-[#0d0d0f] border-white/10 text-white" data-testid="detail-category"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#1C1C1F] border-white/10 text-white max-h-64">{(meta?.categories || []).map((c) => <SelectItem key={c} value={c} className="focus:bg-white/10">{c}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Status">
              <Select value={form.status} onValueChange={(v) => set("status", v)}>
                <SelectTrigger className="bg-[#0d0d0f] border-white/10 text-white" data-testid="detail-status"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#1C1C1F] border-white/10 text-white">{(meta?.statuses || []).map((s) => <SelectItem key={s} value={s} className="focus:bg-white/10">{s}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
          </div>
          <Field label="Folder">
            <Select value={form.folder_id} onValueChange={(v) => set("folder_id", v)}>
              <SelectTrigger className="bg-[#0d0d0f] border-white/10 text-white" data-testid="detail-folder"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#1C1C1F] border-white/10 text-white max-h-64">
                <SelectItem value="root" className="focus:bg-white/10">No folder (root)</SelectItem>
                {folders.map((f) => <SelectItem key={f.folder_id} value={f.folder_id} className="focus:bg-white/10">{f.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Tags (comma separated)"><input value={form.tags} onChange={(e) => set("tags", e.target.value)} data-testid="detail-tags" className={inputCls} placeholder="tax, 2026, llc" /></Field>
          <Field label="Notes"><textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} data-testid="detail-notes" rows={3} className={`${inputCls} resize-none py-2`} /></Field>
          <Field label="Expiration / renewal date"><input type="date" value={form.expiration_date ? form.expiration_date.slice(0, 10) : ""} onChange={(e) => set("expiration_date", e.target.value)} data-testid="detail-expiration" className={inputCls} /></Field>
          <div className="flex items-center gap-3">
            <button onClick={() => set("favorite", !form.favorite)} data-testid="detail-favorite" className={`flex items-center gap-1.5 text-xs px-3 h-9 rounded-full border ${form.favorite ? "border-[#D4AF37]/40 text-[#D4AF37] bg-[#151310]" : "border-white/15 text-[#A1A1AA]"}`}><Star className="w-4 h-4" fill={form.favorite ? "currentColor" : "none"} /> Favorite</button>
            <button onClick={() => set("locked", !form.locked)} data-testid="detail-lock" className={`flex items-center gap-1.5 text-xs px-3 h-9 rounded-full border ${form.locked ? "border-[#D4AF37]/40 text-[#D4AF37] bg-[#151310]" : "border-white/15 text-[#A1A1AA]"}`}><Lock className="w-4 h-4" /> {form.locked ? "Locked" : "Lock"}</button>
          </div>
        </div>
        <Button onClick={save} disabled={saving} data-testid="detail-save-btn" className="w-full mt-2 fv-gold-gradient text-[#0A0A0B] font-semibold rounded-full hover:opacity-90">
          {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…</> : "Save changes"}
        </Button>
      </DialogContent>
    </Dialog>
  );
}

const inputCls = "w-full bg-[#0d0d0f] border border-white/10 rounded-lg px-3 h-10 text-sm text-white focus:outline-none focus:border-[#D4AF37]/40";
function Field({ label, children }) {
  return (<div><label className="text-xs text-[#A1A1AA] mb-1.5 block">{label}</label>{children}</div>);
}
